import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAccent } from '../../hooks/useAccent';
import { ACCENTS, type AccentName } from '../../lib/theme';

const PANEL_MARGIN = 8;
const PANEL_WIDTH = 260;

export function AccentPicker() {
  const { accent, setAccent } = useAccent();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: PANEL_WIDTH });
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const button = buttonRef.current;
      const panel = panelRef.current;
      if (!button || !panel) return;

      const rect = button.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = Math.min(PANEL_WIDTH, viewportWidth - PANEL_MARGIN * 2);
      const panelHeight = panel.offsetHeight;

      let left = rect.right - width;
      left = Math.max(PANEL_MARGIN, Math.min(left, viewportWidth - width - PANEL_MARGIN));

      let top = rect.bottom + PANEL_MARGIN;
      if (top + panelHeight > viewportHeight - PANEL_MARGIN) {
        top = Math.max(PANEL_MARGIN, rect.top - panelHeight - PANEL_MARGIN);
      }

      setPosition({ top, left, width });
    };

    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node) && !panelRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);

    const raf = window.requestAnimationFrame(updatePosition);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`Main color: ${ACCENTS[accent].label}`}
        aria-label="Main color"
        aria-expanded={open}
        className="flex items-center justify-center rounded transition-colors"
        style={{
          width: '30px',
          height: '30px',
          backgroundColor: 'transparent',
          border: '1px solid var(--border)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span
          aria-hidden
          style={{
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: 'var(--accent)',
            boxShadow: 'var(--accent-swatch-ring)',
          }}
        />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Choose main color"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            zIndex: 9999,
            width: position.width,
            maxWidth: `calc(100vw - ${PANEL_MARGIN * 2}px)`,
            padding: '12px',
            borderRadius: '10px',
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border-strong)',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <div
            style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--text-tertiary)',
              fontWeight: 600,
              marginBottom: '10px',
            }}
          >
            Main color
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {(Object.keys(ACCENTS) as AccentName[]).map((name) => {
              const isActive = accent === name;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    setAccent(name);
                    setOpen(false);
                  }}
                  title={ACCENTS[name].label}
                  aria-label={ACCENTS[name].label}
                  aria-pressed={isActive}
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '8px',
                    backgroundColor: ACCENTS[name].previewHex,
                    border: `2px solid ${isActive ? 'var(--text-primary)' : 'transparent'}`,
                    padding: 0,
                    cursor: 'pointer',
                    transition: 'transform 100ms ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                />
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
